import { createContext, useCallback, useContext, useLayoutEffect, useRef, useState, type RefObject } from 'react'
import { bindChatListScroll, readListPosition, saveListPosition } from './chatListScroll'

export const ChatListReturnContext = createContext<string | null>(null)

export function useChatListReturn(): string | null {
  return useContext(ChatListReturnContext)
}

export function useChatListScroll(ref: RefObject<HTMLDivElement | null>, view: string, ready: boolean) {
  const stopRestore = useRef<(() => void) | null>(null)
  useLayoutEffect(() => {
    const list = ref.current
    if (!list || !ready) return
    const binding = bindChatListScroll(list, readListPosition(view), (top) => saveListPosition(view, top))
    stopRestore.current = binding.cancel
    return () => {
      binding.dispose()
      stopRestore.current = null
    }
  }, [ref, view, ready])

  return useCallback((nextView: string) => {
    stopRestore.current?.()
    saveListPosition(nextView, 0)
  }, [])
}

/** Keep expanded Code sections available when their list remounts on Back. */
export function useChatListSection(key: string) {
  const storageKey = `lane-control:chatlist-section:${key}`
  const [open, setOpen] = useState(() => {
    try { return sessionStorage.getItem(storageKey) === 'true' }
    catch { return false }
  })
  const toggle = () => setOpen((current) => {
    try { sessionStorage.setItem(storageKey, String(!current)) }
    catch { /* The current view still works without storage. */ }
    return !current
  })
  return [open, toggle] as const
}
